/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { ContentLargeImageFragment } from "@cosmos/hygraph-schema";
import { useState } from "react";
import {
	Animate,
	BackgroundContainer,
	CosmosClickableBox,
	CosmosTagProps,
	ResponsiveContainer,
	SectionContainer,
	SectionContainerProps,
	SectionHeading,
	Wrap,
} from "@components/elements";
import { css, cx } from "@panda/css";
import { Flex, VStack } from "@panda/jsx";

import { ContentLargeImageMainStack } from "./ContentLargeImage.MainStack";
import { ContentLargeImageMedia } from "./ContentLargeImage.Media";
import { useLocale, useLocales } from "@epic-mw/i18n";
import { isRelative, pathnameHasLocale } from "@cosmos/common-utils";

export interface ContentLargeImageSectionProps extends Omit<ContentLargeImageFragment, "badges"> {
	className?: string;
	sectionIndex?: number;
	enableAnimate?: boolean;
	badges?: CosmosTagProps[] | null;
	date?: string | null;
	sectionContainerProps?: SectionContainerProps;
}

export const ContentLargeImageSection = (props: ContentLargeImageSectionProps | null): React.JSX.Element => {
	const locale = useLocale();
	const locales = useLocales();

	const enableAnimate = props?.enableAnimate !== undefined ? props.enableAnimate : true;
	const isFirstSection = props?.sectionIndex === 0;
	const validSectionTitle = Boolean(props?.config?.heading?.title?.text?.trim());
	const mainStackTitleAs = isFirstSection ? (validSectionTitle ? "h2" : "h1") : validSectionTitle ? "h3" : "h2";

	const buttons = props?.buttons || [];
	let href: string | null = null;
	if (buttons.length === 1 && buttons[0].href) {
		href = buttons[0].href;
		if (isRelative(href)) {
			if (!pathnameHasLocale(locales, href)) {
				href = `/${locale}${href}`;
			}
		}
	}
	const isClickableBox = Boolean(href);

	const [isHoveringBox, setIsHoveringBox] = useState(false);

	return (
		<BackgroundContainer
			bgAnimation={props?.config?.backgroundAnimation}
			bgImageAsset={props?.config?.backgroundImage}
			bgFullHeight={props?.config?.backgroundFullHeight}
			edsBackgroundColor={props?.config?.edsBackgroundColor}
		>
			<ResponsiveContainer>
				<SectionContainer contentVisibility="unset" {...props?.sectionContainerProps}>
					<VStack
						alignItems="stretch"
						gap="eds.dimension.56"
						className={cx("content-large-image-section", props?.className)}
					>
						<Wrap
							key={props?.config?.heading?.title?.text || ""}
							condition={enableAnimate && validSectionTitle ? isFirstSection : false}
							wrap={(children) => <Animate type="slide">{children}</Animate>}
						>
							<SectionHeading
								{...props?.config?.heading}
								title={{ ...props?.config?.heading?.title, as: isFirstSection ? "h1" : "h2" }}
							/>
						</Wrap>
						<div className={css({ position: "relative" })}>
							{isClickableBox ? (
								<CosmosClickableBox.Link
									href={href || ""}
									aria-label={props?.title?.text}
									className={css({
										position: "absolute",
										w: "full",
										h: "full",
										borderRadius: "eds.borderRadius.24",
										outlineOffset: "eds.dimension.16",
										_focusVisible: {
											outline: "2px solid white",
										},
										"&&::before": {
											zIndex: 3,
										},
									})}
									analytics={{
										eventCategory: "tile",
										eventLabel: props?.title?.text || "",
										eventValue: {
											component: "ContentLargeImage",
											href,
										},
									}}
									onMouseEnter={() => {
										setIsHoveringBox(true);
									}}
									onMouseLeave={() => {
										setIsHoveringBox(false);
									}}
								/>
							) : null}

							<Flex
								direction={{ base: props?.flip ? "row-reverse" : "row", lgDown: "column" }}
								justifyContent="space-between"
								columnGap="eds.dimension.64"
								rowGap={{ md: "eds.dimension.80", mdDown: "eds.dimension.48" }}
								className={cx(
									"section-flex",
									css({
										[`& button, & a[href]:not(.clickable-box__link)`]: {
											position: "relative",
											zIndex: 3,
										},
									}),
									isHoveringBox
										? css({
												"& .asset-wrap img, & .asset-wrap video": {
													transform: "scale(1.05)",
												},
											})
										: null,
								)}
							>
								<ContentLargeImageMainStack
									animate={enableAnimate ? isFirstSection : false}
									eyebrow={props?.eyebrow || ""}
									gradient={props?.gradient}
									title={props?.title}
									titleAs={mainStackTitleAs}
									desc={props?.desc}
									buttons={props?.buttons || []}
									date={props?.date}
								/>

								{props?.asset?.url ? (
									<div
										className={cx(
											"asset-wrap",
											css({
												aspectRatio: "16 / 9",
												lg: {
													w: "1/2",
												},
												"--asset-border-radius": `24px`,
											}),
										)}
									>
										<Wrap
											key={props?.asset?.url}
											condition={enableAnimate ? isFirstSection : false}
											wrap={(children) => (
												<Animate delay={0.5} type="slideLeft" customCss={css.raw({ flexBasis: "50%" })}>
													{children}
												</Animate>
											)}
										>
											<ContentLargeImageMedia
												asset={props?.asset}
												blurredImage={props?.blurredImage}
												tags={props?.badges || []}
											/>
										</Wrap>
									</div>
								) : null}
							</Flex>
						</div>
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
